import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, Subject, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { AuthorizationService } from '@portal/shared/auth/authorization/src/lib/authorization.service';
import { UserRoles } from '@portal/shared/auth/authorization/src/lib/enums/user-roles.enum';
import { Features } from 'environments/enums/features.enum';
import { FeatureToggle } from '@portal/shared/auth/feature-toggle/src';
import { CustomDataService } from '@portal/shared/vui-http/src';
import { HttpClient } from '@angular/common/http';
import { HttpUrlGenerator } from '@ngrx/data';
import { CONSTANTS } from '@apps/portal/src/app/core/core.module';
@Injectable({
  providedIn: 'root',
})
export class PetroService extends CustomDataService<any> {
  emailValue;
  loading$: Observable<boolean>;
  permissions: any;
  permissionsUpdated = new Subject<any>();

  dateFilterFlag = false;
  checkHierarchyAPI: Boolean = false;
  isHierarchyData: Boolean = false;
  private setLoading$: BehaviorSubject<boolean> = new BehaviorSubject(false);

  constructor(
    public authService: AuthorizationService,
    private featureToggleService: FeatureToggle,
    http: HttpClient,
    httpUrlGenerator: HttpUrlGenerator,
  ) {
    super(CONSTANTS.DATA_SERVICE.PERMISSIONS_LITE_SERVICE, http, httpUrlGenerator);
    this.loading$ = this.setLoading$.asObservable();
  }

  getPermissions(): Observable<any> {
    return this.execute('GET', `${CONSTANTS.DATA_SERVICE.PERMISSIONS_LITE_SERVICE}`);
  }

  getSitePermissions(serviceId: string): Observable<any> {
    return this.execute('GET', `${CONSTANTS.DATA_SERVICE.PERMISSIONS_LITE_SERVICE}/${serviceId}`);
  }

  getPetroVersion(): Observable<{ version: string } | null> {
    return this.http
      .get<{ build: { version: string } }>(CONSTANTS.DATA_SERVICE.PEMS_VERSION_SERVICE)
      .pipe(
        map((res) => ({ version: res.build.version })),
        catchError(() => of(null)),
      );
  }

  setPermissions(permissions): void {
    this.permissions = permissions;
    this.permissionsUpdated.next({ ...this.permissions });
  }

  setLoading(state: boolean): void {
    this.setLoading$.next(state);
  }

  setUserEmail(value: any): any {
    this.emailValue = value;
  }

  isHelpdeskUser(): boolean {
    return this.authService.getRoles().includes(UserRoles.PetroHelpDeskAdmin);
  }

  isPetroUser(): boolean {
    return (
      this.authService.getRoles().includes(UserRoles.PetroSiteAdmin) ||
      this.authService.getRoles().includes(UserRoles.PetroSiteUser) ||
      this.authService.getRoles().includes(UserRoles.MajorOilAdmin) ||
      this.authService.getRoles().includes(UserRoles.MajorOilUser) ||
      this.authService.getRoles().includes(UserRoles.PetroPartnerAdmin) ||
      this.authService.getRoles().includes(UserRoles.PetroHelpDeskAdmin) ||
      this.authService.getRoles().includes(UserRoles.CommanderOnboarder) ||
      this.authService.getRoles().includes(UserRoles.PetroSalesforceAdmin) ||
      this.authService.getRoles().includes(UserRoles.PetroMerchantOnboarder) ||
      this.authService.getRoles().includes(UserRoles.PetroVascAdmin) ||
      this.authService.getRoles().includes(UserRoles.PetroVascUser)
    );
  }

  isPartnerUser(): boolean {
    return this.authService.getRoles().includes(UserRoles.PetroPartnerAdmin);
  }

  isVascAdmin(): boolean {
    return this.authService.getRoles().includes(UserRoles.PetroVascAdmin);
  }

  isAdminUser(): boolean {
    return (
      this.authService.getRoles().includes(UserRoles.PetroSiteAdmin) ||
      this.authService.getRoles().includes(UserRoles.MajorOilAdmin)
    );
  }

  isSubUser(): boolean {
    return (
      this.authService.getRoles().includes(UserRoles.PetroSiteUser) ||
      this.authService.getRoles().includes(UserRoles.MajorOilUser) ||
      this.authService.getRoles().includes(UserRoles.PetroVascUser)
    );
  }

  isMultiUser(): boolean {
    return (
      this.authService.getRoles().includes(UserRoles.PetroSiteAdmin) &&
      this.authService.getRoles().includes(UserRoles.MajorOilAdmin)
    );
  }

  isPetroAdminOrUser(): boolean {
    return (
      this.authService.getRoles().includes(UserRoles.PetroSiteAdmin) ||
      this.authService.getRoles().includes(UserRoles.PetroSiteUser)
    );
  }

  isVascAdminOrUser(): boolean {
    return (
      this.authService.getRoles().includes(UserRoles.PetroVascAdmin) ||
      this.authService.getRoles().includes(UserRoles.PetroVascUser)
    );
  }

  isMajorOilAdminOrUser(): boolean {
    return (
      this.authService.getRoles().includes(UserRoles.MajorOilAdmin) ||
      this.authService.getRoles().includes(UserRoles.MajorOilUser)
    );
  }

  isPetroSiteAdmin(): boolean {
    return this.authService.getRoles().includes(UserRoles.PetroSiteAdmin);
  }

  isMajorOilAdmin(): boolean {
    return this.authService.getRoles().includes(UserRoles.MajorOilAdmin);
  }

  isMajorOilUser(): boolean {
    return this.authService.getRoles().includes(UserRoles.MajorOilUser);
  }

  isExternalAccessUser(): boolean {
    return (
      this.authService.getRoles().includes(UserRoles.PetroPartnerAdmin) ||
      this.authService.getRoles().includes(UserRoles.PetroVascAdmin)
    );
  }

  getPermissionListener(): Observable<any> {
    return this.permissionsUpdated.asObservable();
  }

  getPermissionObject(): any {
    return this.permissions;
  }

  showReportsMenu(): boolean {
    return (
      this.permissions?.permission_menu?.reports?.some(
        (i: any) => i.view_access || i.edit_access,
      ) ?? false
    );
  }

  showAdministrationMenu(): boolean {
    return (
      this.permissions?.permission_menu?.administration?.some(
        (i: any) => i.view_access || i.edit_access,
      ) ?? false
    );
  }

  showSiteAssetsMenu(): boolean {
    if (this.featureToggleService.isFeatureActive(Features.SiteAssets)) {
      const reports = this.permissions?.permission_menu?.reports
        ? this.permissions?.permission_menu?.reports?.filter((i) => i.sub_menu === 'siteAssets')
        : '';
      return reports.length ? reports[0].view_access || reports[0].edit_access : false;
    }
    return false;
  }

  showPetroAuditLogsMenu(): boolean {
    if (this.featureToggleService.isFeatureActive(Features.PetroAuditLogs)) {
      const reports = this.permissions?.permission_menu?.reports
        ? this.permissions?.permission_menu?.reports?.filter((i) => i.sub_menu === 'auditLogs')
        : '';
      return reports.length ? reports[0].view_access || reports[0].edit_access : false;
    }
    return false;
  }

  showTlogMenu(): boolean {
    if (
      (this.isPetroAdminOrUser() || this.isMajorOilAdminOrUser()) &&
      this.featureToggleService.isFeatureActive(Features.TlogViewer)
    ) {
      const tlog = this.permissions?.permission_menu?.reports
        ? this.permissions?.permission_menu?.reports?.filter((i) => i.sub_menu === 'tlog')
        : '';
      return tlog.length ? tlog[0].view_access || tlog[0].edit_access : false;
    }
    return false;
  }

  showReports(): boolean {
    if (
      (this.authService.getRoles().includes(UserRoles.PetroSiteAdmin) ||
        this.authService.getRoles().includes(UserRoles.PetroSiteUser)) &&
      this.featureToggleService.isFeatureActive(Features.Reports)
    ) {
      const reports = this.permissions?.permission_menu?.reports
        ? this.permissions?.permission_menu?.reports?.filter((i) => i.sub_menu === 'reports')
        : '';
      return reports.length ? reports[0].view_access || reports[0].edit_access : false;
    }
    return false;
  }

  showAccountsMenu(): boolean {
    if (this.featureToggleService.isFeatureActive(Features.Accounts)) {
      const accounts = this.permissions?.permission_menu?.administration?.filter(
        (i) => i.sub_menu === 'accounts',
      );
      return accounts?.length ? accounts[0].view_access || accounts[0].edit_access : false;
    }
    return false;
  }

  showRolesMenu(): boolean {
    if (this.featureToggleService.isFeatureActive(Features.Roles)) {
      const roles = this.permissions?.permission_menu?.administration?.filter(
        (i) => i.sub_menu === 'roles',
      );
      return roles?.length ? roles[0].view_access || roles[0].edit_access : false;
    }
    return false;
  }

  showHierarchyMenu(): boolean {
    if (this.featureToggleService.isFeatureActive(Features.Hierarchy)) {
      const hierarchy = this.permissions?.permission_menu?.administration?.filter(
        (i) => i.sub_menu === 'hierarchy',
      );
      return hierarchy?.length ? hierarchy[0].view_access || hierarchy[0].edit_access : false;
    }
    return false;
  }

  showUsersMenu(): boolean {
    if (this.featureToggleService.isFeatureActive(Features.Users) && this.isPetroUser()) {
      const users = this.permissions?.permission_menu?.administration?.filter(
        (i) => i.sub_menu === 'users',
      );
      return users?.length ? users[0].view_access || users[0].edit_access : false;
    }
    return false;
  }

  showSitesMenu(): boolean {
    if (this.featureToggleService.isFeatureActive(Features.Sites)) {
      const site = this.permissions?.permission_menu?.administration?.filter(
        (i) => i.sub_menu === 'sites',
      );
      return site?.length ? site[0].view_access || site[0].edit_access : false;
    }
    return false;
  }

  hasSitesEditAccess(): boolean {
    if (!this.isSubUser()) return true;
    const sites = this.permissions?.permission_menu?.administration?.filter(
      (i: any) => i.sub_menu === 'sites',
    );
    return sites?.length ? !!sites[0]?.edit_access : false;
  }

  // Bulk site actions are limited to admins and their direct sub users (Petro Site
  // User / Major Oil User) - VASC and Partner roles never get this, regardless of
  // their sites edit_access or hierarchy_root_access.
  hasBulkSiteFeatureAccess(): boolean {
    if (this.isAdminUser()) return true;
    const isEligibleSubUser =
      this.authService.getRoles().includes(UserRoles.PetroSiteUser) ||
      this.authService.getRoles().includes(UserRoles.MajorOilUser);
    if (!isEligibleSubUser) return false;

    const sites = this.permissions?.permission_menu?.administration?.find(
      (i: any) => i.sub_menu === 'sites',
    );
    return !!(sites?.edit_access && this.permissions?.permission_menu?.hierarchy_root_access);
  }

  hasHierarchyEditAccess(): boolean {
    if (!this.isSubUser()) return true;
    const hierarchy = this.permissions?.permission_menu?.administration?.filter(
      (i: any) => i.sub_menu === 'hierarchy',
    );
    return hierarchy?.length ? !!hierarchy[0]?.edit_access : false;
  }

  hasDeviceSwapAccess(): boolean {
    const deviceSwap = this.permissions?.permission_menu?.administration?.filter(
      (i) => i.sub_menu === 'deviceSwap',
    );
    return deviceSwap?.length ? deviceSwap[0].view_access || deviceSwap[0].edit_access : false;
  }

  showServicesMenu(): boolean {
    return (
      this.authService.getRoles().includes(UserRoles.PetroSiteAdmin) &&
      this.featureToggleService.isFeatureActive(Features.CommanderFleetService)
    );
  }

  showExternalAccessMenu(): boolean {
    return this.authService.getRoles().includes(UserRoles.PetroSiteAdmin);
  }

  // for mobile payment and loyalty config
  showPaymentConfigurationMenu(): boolean {
    return this.hasReadOrWritePermissionForPaymentPartnerConfigs();
  }

  showTableConfig(): boolean {
    if (this.isHelpdeskUser()) return false;
    return this.hasReadOrWritePermissionForPaymentPartnerConfigs();
  }

  showPetroPaymentPartnerConfigurationMenu(): boolean {
    if (this.featureToggleService.isFeatureActive(Features.MobilePayment)) {
      return this.hasReadOrWritePermissionForPaymentPartnerConfigs();
    }
    return false;
  }

  showEnterpriseMenu(): boolean {
    return this.isPetroAdminOrUser() || this.isMajorOilAdminOrUser();
  }

  showTablesMenu(): boolean {
    if (this.featureToggleService.isFeatureActive(Features.ViperTable)) {
      return true;
    }
  }

  showSiteView(): boolean {
    if (this.featureToggleService.isFeatureActive(Features.ViperSiteView)) {
      return true;
    }
  }

  showLoyalty(): boolean {
    return this.featureToggleService.isFeatureActive(Features.Loyalty);
  }

  isPaymentPartnerConfigurationEditPermission(): boolean {
    if (this.isAdminUser()) {
      return true;
    }
    if (this.permissions) {
      const paymentPartnerConfig = this.permissions?.permission_menu?.configuration?.find(
        (item: any) =>
          ['paymentConfiguration', 'paymentPartnerConfiguration'].includes(item.sub_menu),
      );
      return paymentPartnerConfig?.edit_access || false;
    }
    return false;
  }

  hasMidToggleAccess(): boolean {
    const midToggle = this.permissions?.permission_menu?.additionalSetting?.filter(
      (i: any) => i.sub_menu === 'midToggle',
    );
    return midToggle?.length ? midToggle[0].view_access || midToggle[0].edit_access : false;
  }

  showMajorOilSettings(): boolean {
    if (this.featureToggleService.isFeatureActive(Features.MajorOilSettings)) {
      return this.isMajorOilAdmin() && this.hasMidToggleAccess();
    }
    return false;
  }

  showSiteConfig(): boolean {
    if (this.featureToggleService.isFeatureActive(Features.SiteConfiguration)) {
      return (
        this.permissions?.permission_menu?.configuration?.some(
          (i: any) => i.view_access || i.edit_access,
        ) ?? false
      );
    }
    return false;
  }

  showEnterpriseConfig(): boolean {
    if (this.featureToggleService.isFeatureActive(Features.EnterpriseConfiguration)) {
      if (this.isPetroAdminOrUser() || this.isMajorOilAdminOrUser()) {
        if (this.permissions) {
          const paymentConfigPermissions = this.permissions?.permission_menu?.configuration?.filter(
            (i: any) =>
              ['paymentConfiguration', 'paymentPartnerConfiguration'].includes(i.sub_menu),
          );
          return paymentConfigPermissions?.length
            ? paymentConfigPermissions.some((p: any) => p.edit_access || p.view_access)
            : false;
        }
        return false;
      }
    }
  }

  showPolicyManagement(): boolean {
    if (this.featureToggleService.isFeatureActive(Features.PolicyManagement)) {
      return this.authService.getRoles().includes(UserRoles.MajorOilAdmin);
    }
  }

  showTerms(): void {
    if (this.isPartnerUser()) {
      window.open(
        'assets/html/petro/VF_Petro_and_Convenience_Retail_Site_Solution_T&Cs_PARTNER_T&Cs.pdf',
        '_blank',
        'noopener,noreferrer',
      );
    } else {
      window.open(
        'assets/html/petro/Verifone_Petroleum_and_Convenience_Retail_Site_Solution_Terms_and_Conditions_(Effective_Apr_22_2026_for_Publication).pdf',
        '_blank',
        'noopener,noreferrer',
      );
    }
  }

  showLicenseInformation(): void {
    const licenseInformationPdf = 'assets/html/petro/Third-Party-Software-Information.pdf';
    window.open(licenseInformationPdf, '_blank', 'noopener,noreferrer');
  }

  hasReadOrWritePermissionForPaymentPartnerConfigs(): boolean {
    if (this.isAdminUser()) {
      return true;
    }

    if (this.permissions) {
      const paymentConfigPermissions = this.permissions?.permission_menu?.configuration?.filter(
        (i: any) => ['paymentConfiguration', 'paymentPartnerConfiguration'].includes(i.sub_menu),
      );
      return paymentConfigPermissions?.length
        ? paymentConfigPermissions.some((p: any) => p.edit_access || p.view_access)
        : false;
    }
    return false;
  }
  showLogPullMenu(): boolean {
    return this.isHelpdeskUser();
  }

  showLogLevelMenu(): boolean {
    return this.isHelpdeskUser();
  }

}
